// Subir una imagen (banners, publicidad, logo, foto de farmacia) con el estilo del panel
// (.img-slot de la maqueta). Se optimiza en el navegador antes de subir, salvo los GIF.
// Guardado visible: optimizando / subiendo / error (§4.8).
import React, { useRef, useState } from "react";
import { Image as ImageIcon, Monitor, Smartphone } from "lucide-react";
import { compressImage, formatBytes } from "../imageOptimizer";
import { adminFetch } from "../api";
import { Button, ICON, Input, Spinner, Toggle, cx } from "./ui";

const ICONS = { image: ImageIcon, monitor: Monitor, smartphone: Smartphone };

export const ImageUploader: React.FC<{
  label: string;
  value: string;
  onChange: (url: string) => void;
  /** se mantiene por compatibilidad; ya no hace falta */
  idPrefix?: string;
  /** medida ideal, se muestra cuando no hay imagen */
  hint?: string;
  /** qué pasa si queda vacía, por ejemplo «Usa la de escritorio» */
  emptyText?: string;
  icon?: keyof typeof ICONS;
  /** miniatura vertical (celular, módulo 320 × 720) */
  tall?: boolean;
  /** muestra un campo para pegar el link de la imagen */
  allowUrl?: boolean;
  showCompressionToggle?: boolean;
  defaultCompress?: boolean;
}> = ({ label, value, onChange, hint, emptyText, icon = "image", tall, allowUrl, showCompressionToggle, defaultCompress = true }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<"idle" | "optimizing" | "uploading">("idle");
  const [error, setError] = useState<string | null>(null);
  const [shouldCompress, setShouldCompress] = useState(defaultCompress);
  const [stats, setStats] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const Icon = ICONS[icon];
  const busy = step !== "idle";

  const uploadFile = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      setError("Eso no es una imagen. Usá PNG, JPG, WEBP o GIF.");
      return;
    }
    setError(null);
    setStats(null);
    let fileToUpload = file;
    let sizeText: string | null = null;
    const isGif = file.type === "image/gif" || file.name.toLowerCase().endsWith(".gif");
    const compressThis = shouldCompress && !isGif;
    try {
      if (compressThis) {
        setStep("optimizing");
        try {
          const opt = await compressImage(file, { maxWidth: 1200, maxHeight: 1200, quality: 0.75, convertTo: "image/webp" });
          fileToUpload = opt.file;
          if (opt.ratio > 0) sizeText = `${formatBytes(opt.compressedSize)} · ${opt.ratio} % menos`;
        } catch {
          // si no se puede optimizar, se sube la original
        }
      }
      setStats(sizeText || `${formatBytes(fileToUpload.size)}${isGif ? " · GIF sin tocar" : ""}`);
      setStep("uploading");
      const formData = new FormData();
      formData.append("photo", fileToUpload);
      formData.append("compress", compressThis ? "true" : "false");
      const res = await adminFetch("/api/upload-photo", { method: "POST", body: formData });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.url) throw new Error(data?.error || `El servidor respondió ${res.status}.`);
      onChange(data.url);
    } catch (err: any) {
      setStats(null);
      setError(`No se subió: ${err?.message || err}`);
    } finally {
      setStep("idle");
    }
  };

  const status = busy
    ? step === "optimizing"
      ? "Optimizando…"
      : "Subiendo…"
    : value
      ? stats || "Cargada"
      : emptyText || hint || "Sin imagen";

  return (
    <div className="flex flex-col gap-2">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const file = e.dataTransfer.files?.[0];
          if (file) uploadFile(file);
        }}
        className={cx(
          "flex items-center gap-3 rounded-xl border p-2.5 transition-colors",
          dragOver ? "border-adm-acento bg-adm-acento-suave" : error ? "border-adm-rojo-borde bg-adm-rojo-suave" : "border-adm-border bg-adm-card",
        )}
      >
        <div
          className={cx(
            "grid shrink-0 place-items-center overflow-hidden rounded-md border border-adm-border bg-adm-surface text-adm-t40",
            tall ? "h-14 w-9" : "h-9 w-[88px]",
          )}
        >
          {busy ? (
            <Spinner />
          ) : value ? (
            <img src={value} alt="" className="h-full w-full object-contain" referrerPolicy="no-referrer" />
          ) : (
            <Icon {...ICON} />
          )}
        </div>
        <div className="min-w-0 flex-1 text-[13px]">
          <div className="font-medium text-adm-text">{label}</div>
          <div className={cx("truncate", error ? "text-adm-rojo" : "text-adm-t55")}>{error || status}</div>
          {value && !busy && hint && <div className="truncate text-xs text-adm-t40">{hint}</div>}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {value && !busy && (
            <Button variant="ghost" tone="danger" size="sm" onClick={() => { onChange(""); setStats(null); }}>
              Quitar
            </Button>
          )}
          <Button size="sm" disabled={busy} onClick={() => inputRef.current?.click()}>
            {value ? "Cambiar" : "Subir"}
          </Button>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          aria-label={`${label}: elegir archivo`}
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (file) uploadFile(file);
          }}
        />
      </div>
      {showCompressionToggle && (
        <Toggle checked={shouldCompress} onChange={setShouldCompress} label="Comprimir (apagalo para GIF animados)" />
      )}
      {allowUrl && (
        <Input
          inputMode="url"
          aria-label={`${label}: link de la imagen`}
          placeholder="O pegá el link de la imagen"
          value={value}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        />
      )}
    </div>
  );
};
